import * as React from 'react';
import cx from 'classnames';
import { Router } from '../../configs/routes';
import schedulePaymentStyles from '../../styles/paymentSchedule/schedulePayment.scss';
import styles from '../../styles/plan/paymentFundsSummary.scss';
import { ERROR_MESSAGES, Direction } from '../../constants';
import { Api } from '../../api';
import { showError } from '../../utils';
import { handleTrack } from '../../utils/trackActions';

const api = new Api();

const tpvBankAlgoliaIndex = 'TPV_enabled_banks';

interface Props {
  hideEdit?: boolean;
  bankAccounts: Record<string, any>[];
  investmentOwnerCode: string;
  investmentOwnerCodeHasError: boolean;
  handleInvestmentOwner: (e: React.FormEvent<HTMLSelectElement>) => void;
  toggleInvestmentOwnerCodeHasError: (value: boolean) => void;
  SipDateHasError: boolean;
  toggleSIPDateSelectorHasError: (value: boolean) => void;
  updateBankId: (val: number, cb: Function) => void;
  updateBankTpvData: (data: Record<string, any>) => void;
  personalDetails: any;
  kycCheck: any;
  investmentType: Direction;
  sipDates: string[];
  trackLabel?: string;
  selectedBankId: number;
  handleScheduleBtn?: () => void;
  isNoBankVerified?: boolean;
}

interface State {
  viewFunds: boolean;
  selectedSipDate: string;
  bankTpvData: Record<string, any>;
}

class PaymentFundsSummaryNew extends React.Component<Props, State> {
  public state: State = {
    viewFunds: false,
    selectedSipDate: '',
    bankTpvData: null,
  };

  private getCurrentBankData = (value: number) => {
    return this.props.bankAccounts.filter((item) => item.id === value)[0];
  };

  private setBankTpvData(value: number) {
    const selectedbank = this.getCurrentBankData(value);

    const bankCode = (selectedbank && selectedbank.ifscCode.substr(0, 4)) || '';

    api.searchTPVBank(bankCode, tpvBankAlgoliaIndex).then((res) => {
      const results = res.hits[0];
      this.setState({
        bankTpvData: results,
      });
      this.props.updateBankTpvData(results);
    });
  }

  public async componentDidMount() {
    if (this.props.investmentType === Direction.SIP) {
      const nearestSIPDate = this.getNearestSIPDate();
      this.setState(
        {
          selectedSipDate: nearestSIPDate,
        },
        this.editSipDate
      );
    }
    this.setBankTpvData(this.props.selectedBankId);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: Props) {
    // rerender if selectedBankId changes
    if (nextProps.selectedBankId !== this.props.selectedBankId) {
      this.setState({
        ...this.state,
      });
    }
  }

  private getNearestSIPDate = (): string => {
    const today = new Date().getDate();
    if (today < 7) {
      return '05';
    } else if (today > 16) {
      return '20';
    } else {
      return '10';
    }
  };

  private trackClick(label: string, evtObj: any, path?: string) {
    const trackingLabel =
      this.props.trackLabel || 'invest_flow_payment_summary';
    handleTrack({
      label: `${trackingLabel}_${label}`,
      obj: evtObj,
      url: path,
    });
  }

  private updateUrlParameter = (url: string, param: string, value: string) => {
    const regex = new RegExp('(' + param + '=)[^&]+');
    if (url.match(regex)) {
      return url.replace(regex, '$1' + value);
    } else {
      return url + '&sipDate=' + value;
    }
  };

  private handleSipDateChange = (value: string) => {
    this.setState(
      {
        selectedSipDate: value,
      },
      this.editSipDate
    );
    const path = this.updateUrlParameter(location.href, 'sipDate', value);

    this.trackClick(`dropdown_sip_date_${value}`, null, path);
    Router.pushRoute(path);
  };

  private editSipDate = async () => {
    const { selectedSipDate } = this.state;
    const { toggleSIPDateSelectorHasError } = this.props;
    if (selectedSipDate === '') {
      toggleSIPDateSelectorHasError(true);
      return;
    }

    const apiResponse = await api.editSipDate(selectedSipDate);
    const { success } = apiResponse || {};
    if (!success) {
      toggleSIPDateSelectorHasError(true);
      showError(ERROR_MESSAGES.generic);
    } else {
      toggleSIPDateSelectorHasError(false);
    }
  };

  private handleBankChange(evt: React.FormEvent<HTMLSelectElement>) {
    const { value } = evt.target;
    const { handleScheduleBtn } = this.props;
    if (value === 'addNew') {
      Router.push(`/save/profile?modal=addBank`); // Facing issue in pushRoute, so used push
      return;
    }
    if (value) {
      this.props.updateBankId(Number(value), handleScheduleBtn);
      this.setBankTpvData(Number(value));
    }
  }

  private getMaskedAccNum() {
    const selectedbank = this.getCurrentBankData(this.props.selectedBankId);
    return (selectedbank && selectedbank.accountNo.substr(-4)) || '--';
  }

  private getTpvBankMsg() {
    const { bankTpvData } = this.state;
    let msg = '';
    if (
      bankTpvData &&
      bankTpvData.NetBankingPresent === 1 &&
      bankTpvData.UPIPresent === 0
    ) {
      msg = 'via Netbanking only';
    } else if (
      bankTpvData &&
      bankTpvData.NetBankingPresent === 0 &&
      bankTpvData.UPIPresent === 1
    ) {
      msg = 'via UPI only';
    }
    return msg;
  }

  public render() {
    const selectedClass = styles['selected'];
    const {
      bankAccounts,
      sipDates,
      selectedBankId,
      investmentType,
      isNoBankVerified,
      kycCheck,
    } = this.props;
    const { selectedSipDate } = this.state;
    const { allowedToInvest, kycStatus = '' } = kycCheck;
    const showKycError =
      typeof allowedToInvest === 'boolean' && !allowedToInvest;
    const kycStatusUpdated = kycStatus.split('_').join(' ');
    return (
      <div className="row">
        <div
          className={cx('columns mt20 flex', styles['bank-sipdate-wrapper'])}
        >
          <div className="columns large-6 small-12 lil-mb-4">
            <span>Selected bank for payment</span>
            <select
              className={cx(
                schedulePaymentStyles['select-dd'],
                'form-control mt10'
              )}
              value={selectedBankId}
              onChange={this.handleBankChange.bind(this)}
            >
              {bankAccounts.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.nameOfBank || ''}
                </option>
              ))}

              <option value={'addNew'}> + Add new</option>
            </select>
          </div>
          {investmentType === Direction.SIP && (
            <div className="columns large-6 small-12 lil-mb-4">
              <span>Next SIP installment date</span>
              <div className={cx('flex mt10', styles['sipdate-wrapper'])}>
                {sipDates.map((date) => {
                  return (
                    <div
                      key={date}
                      className={cx({
                        [selectedClass]: date === selectedSipDate,
                      })}
                      onClick={this.handleSipDateChange.bind(this, date)}
                    >
                      {date}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {isNoBankVerified && (
          <div className="columns lil-text-red lil-text-s-12 lil-mb-3">
            <span className="lil-font-bold lil-pl-3">
              "Account Verification Pending".
            </span>{' '}
            Please Verify/Update your Bank Details to make the Payment.
          </div>
        )}
        {showKycError && (
          <div
            className="columns lil-text-red lil-text-s-12 lil-m-4 lil-mt-0"
            style={{ paddingRight: '32px' }}
          >
            We received KYC -
            <span className="lil-font-bold lil-px-1">{kycStatusUpdated}</span>
            from the authorities. Please update your KYC as it is mandatory to
            complete your investment.
          </div>
        )}
        {!isNoBankVerified && (
          <div className={cx('columns', styles['bank-msg-warning'])}>
            {`Please make sure you are using Bank Account ending in 
          ${this.getMaskedAccNum()} to make the payment ${this.getTpvBankMsg()}`}
          </div>
        )}
      </div>
    );
  }
}

export default PaymentFundsSummaryNew;



// WEBPACK FOOTER //
// ./components/plan/paymentFundsSummaryNew.tsx